import { graphql } from '@web/graphql-queries/index'
import useGraphQLClient, { getGraphQLClient } from './hooks/useGraphQLClient'
import { usePageStore } from './hooks/usePageStore'
import type {
  GetThankYouPageIdFromPageIdQuery,
  PageQuery,
  Page,
  QuickPayValuesInput,
  SubmitFormValuesInput,
  SubmitPostDonationFormValuesInput,
} from '@web/graphql-queries/graphql'
import { useMutation } from '@tanstack/react-query'

export const logProfileOut = () => fetch('/api/profile/login', { method: 'DELETE' })

export const authenticateProfileDocument = graphql(/* GraphQL */ `
  mutation AuthenticateProfile($charityId: ID!, $campaignId: ID!, $encodedToken: String!) {
    authenticateProfile(charityId: $charityId, campaignId: $campaignId, encodedToken: $encodedToken) {
      token
      profileUrl
    }
  }
`)

export const pageQueryDocument = graphql(`
  query Page($pageId: ID!, $campaignId: ID, $profileId: ID) {
    page(id: $pageId, campaignId: $campaignId, profileId: $profileId) {
      ...donationPageLayout
    }
  }
`)

export const campaignLandingPageIdQueryDocument = graphql(`
  query CampaignLandingPageId($campaignId: ID!, $isLiveMode: Boolean!, $pageBucketId: ID!) {
    campaignLandingPageId(campaignId: $campaignId, isLiveMode: $isLiveMode, pageBucketId: $pageBucketId)
  }
`)

export const searchBySlugQueryDocument = graphql(`
  query SearchBySlug($url: String!, $isLiveMode: Boolean!) {
    searchBySlug(url: $url, isLiveMode: $isLiveMode) {
      charityId
      campaignId
      pageId
      pageBucketId
      profileId
      profileData {
        name
        goal
        logoUrl
      }
      campaignSettings {
        campaignDomain
        isPeerToPeerGivingNotificationEnabled
      }
      layout {
        ...donationPageLayout
      }
      redirectUrl
    }
  }
`)

export const donationPageLayoutFragment = graphql(`
  fragment donationPageLayout on Page {
    id
    branding
    header {
      id
      type
      hasDonateButton
      styles
      mobileStyles
    }
    footer {
      id
      type
      taxDeductibleText
      keyInformationText
      styles
      mobileStyles
    }
    components {
      ... on PageOptions {
        id
        type
        styles
        mobileStyles
        siteTitle
        faviconUrl
      }
      ... on Row {
        id
        type
        styles
        mobileStyles
      }
      ... on Column {
        id
        type
        styles
        mobileStyles
      }
      ... on Button {
        id
        type
        buttonText
        url
        target
        buttonType
        styles
        mobileStyles
      }
      ... on DollarHandles {
        id
        type
        dollarHandles {
          amount
          description
          imageUrl
          hasImage
          hasDescription
        }
        styles
        mobileStyles
      }
      ... on ProgressBar {
        id
        type
        goals
        bonusGoals {
          text {
            textValue
            htmlText
          }
        }
        start
        end
        title
        text {
          textValue
          htmlText
        }
        hideOnEnd
        hideBeforeStart
        hideOnGoalReached
        styles
        mobileStyles
        isAllPagesLinked
        linkedPageIds
        offset
        hideWhenMatchedGivingActive
      }
      ... on Image {
        id
        type
        url
        alt
        mergeFieldId
        styles
        mobileStyles
      }
      ... on Text {
        id
        type
        textValue
        htmlText
        styles
        mobileStyles
      }
      ... on Empty {
        id
        type
        styles
        mobileStyles
      }
      ... on DonationForm {
        id
        type
        title
        donationFrequency
        donationFrequencyDefault
        dollarHandlesDefault
        matchedGivings {
          id
          startAt
          endAt
          rounds {
            endFundRange
            multiplier
          }
        }
        dollarHandles {
          amount
          description
          imageUrl
          isPersonalised
          hasImage
          hasDescription
        }
        extraFields {
          type
          name
          label
          isRequired
          order
          defaultValue
          placeholder
          conditionalFields {
            type
            name
            label
            isRequired
            order
            defaultValue
            placeholder
            customField {
              id
              name
              elementType
              fieldDescription
              acceptedValues {
                key
                value
              }
            }
          }
          customField {
            id
            name
            elementType
            fieldDescription
            acceptedValues {
              key
              value
            }
          }
        }
        legalUrl
        styles
        mobileStyles
        minimumAmount
      }
      ... on Video {
        id
        type
        styles
        mobileStyles
        url
        title
      }
      ... on CountdownTimer {
        id
        type
        start
        end
        hideOnEnd
        hideBeforeStart
        finish
        styles
        mobileStyles
        bonusFinishTimes
      }
      ... on Comments {
        id
        type
        showAmount
        showMessage
        showTotal
        showViewAll
        layout
        styles
        mobileStyles
        start
        end
        hideBeforeStart
        hideOnEnd
        isAllPagesLinked
        linkedPageIds
        amountToShowOver
      }
      ... on Footer {
        id
        type
      }
      ... on Header {
        id
        type
      }
      ... on SocialShare {
        id
        type
        hasLink
        hasTwitter
        hasFacebook
        hasEmail
        hasWhatsApp
        hasLinkedIn
        styles
        mobileStyles
      }
      ... on FundraiserButton {
        id
        type
      }
      ... on LoginForm {
        id
        type
        subComponents
        styles
        mobileStyles
      }
      ... on RegisterForm {
        id
        type
        subComponents
        styles
        mobileStyles
      }
      ... on FundraiserProfile {
        id
        type
        subComponents
        styles
        mobileStyles
      }
      ... on Leaderboard {
        id
        type
        subComponents
        styles
        mobileStyles
      }
      ... on ProgressImage {
        id
        type
        styles
        mobileStyles
        subComponents
      }
      ... on VirtualGiftSection {
        id
        type
        styles
        mobileStyles
        subComponents
      }
      ... on PostDonationForm {
        id
        type
        buttonText
        subComponents
        extraFields {
          type
          name
          label
          isRequired
          isHidden
          order
          defaultValue
          placeholder
          conditionalFields {
            type
            name
            label
            isRequired
            isHidden
            order
            defaultValue
            placeholder
            customField {
              id
              name
              elementType
              fieldDescription
              acceptedValues {
                key
                value
              }
            }
          }
          customField {
            id
            name
            elementType
            fieldDescription
            acceptedValues {
              key
              value
            }
          }
        }
        styles
        mobileStyles
      }
    }
    desktop {
      id
      childComponents {
        id
        childComponents {
          id
        }
      }
    }
    mobile {
      id
      childComponents {
        id
        childComponents {
          id
        }
      }
    }
    elementSettings {
      progressBar {
        pageIds
        progressBarGoals {
          goal
        }
      }
      matchedGivings {
        id
        startAt
        endAt
        rounds {
          endFundRange
          multiplier
          startFundRange
        }
      }
    }
  }
`)

export const getThankYouPageIdFromPageIdDocument = graphql(/* GraphQL */ `
  query GetThankYouPageIdFromPageId($campaignId: ID!, $pageId: ID!) {
    thankYouPageIdFromId(campaignId: $campaignId, pageId: $pageId)
  }
`)

export const prefetchThankYouPageLayout = async () => {
  const graphQLClient = getGraphQLClient()
  const { campaignId, pageId } = usePageStore.getState()

  const { thankYouPageIdFromId } = await graphQLClient<GetThankYouPageIdFromPageIdQuery>({
    document: getThankYouPageIdFromPageIdDocument,
    options: { campaignId, pageId },
  })

  const { page } = await graphQLClient<PageQuery>({
    document: pageQueryDocument,
    options: { pageId: thankYouPageIdFromId },
  })
  const layout = page as Page
  usePageStore.setState({ thankYouPageLayout: layout })
}

const submitQuickPayValuesDocument = graphql(/* GraphQL */ `
  mutation SubmitQuickPayValues($input: QuickPayValuesInput!) {
    submitQuickPayValues(input: $input)
  }
`)

export const useSubmitQuickPayValuesMutation = () => {
  const graphQLClient = useGraphQLClient()

  return useMutation({
    mutationFn: (input: QuickPayValuesInput) =>
      graphQLClient({
        document: submitQuickPayValuesDocument,
        options: {
          input,
        },
      }),
  })
}

const submitFormValuesDocument = graphql(/* GraphQL */ `
  mutation SubmitFormValues($input: SubmitFormValuesInput!) {
    submitFormValues(input: $input)
  }
`)

export const useSubmitFormValuesMutation = () => {
  const graphQLClient = useGraphQLClient()

  return useMutation({
    mutationFn: (input: SubmitFormValuesInput) =>
      graphQLClient({
        document: submitFormValuesDocument,
        options: {
          input,
        },
      }),
  })
}

const submitPostDonationFormValuesDocument = graphql(/* GraphQL */ `
  mutation SubmitPostDonationFormValues($input: SubmitPostDonationFormValuesInput!) {
    submitPostDonationFormValues(input: $input)
  }
`)

export const useSubmitPostDonationFormValuesMutation = () => {
  const graphQLClient = useGraphQLClient()

  return useMutation({
    mutationFn: (input: SubmitPostDonationFormValuesInput) =>
      graphQLClient({
        document: submitPostDonationFormValuesDocument,
        options: {
          input,
        },
      }),
  })
}

export const progressBarAmountDocument = graphql(`
  query ProgressBarAmount(
    $campaignId: ID!
    $isAllPagesLinked: Boolean!
    $pageIds: [ID!]
    $isLiveMode: Boolean
    $profileId: ID
  ) {
    progressBarAmount(
      campaignId: $campaignId
      isAllPagesLinked: $isAllPagesLinked
      pageIds: $pageIds
      isLiveMode: $isLiveMode
      profileId: $profileId
    ) {
      amount
    }
  }
`)

export const profileDataDocument = graphql(/* GraphQL */ `
  query ProfileData($charityId: ID!, $campaignId: ID!, $profileId: ID!) {
    profileData(charityId: $charityId, campaignId: $campaignId, profileId: $profileId) {
      name
      logoUrl
      description
      goal
      isGivingNotificationEnabled
    }
  }
`)

export const leaderboardDocument = graphql(/* GraphQL */ `
  query Leaderboard(
    $charityId: ID!
    $campaignId: ID!
    $isLiveMode: Boolean!
    $orderBy: ProfileQueryOrderType!
    $isApprovedOnly: Boolean!
  ) {
    leaderboard(
      charityId: $charityId
      campaignId: $campaignId
      isLiveMode: $isLiveMode
      orderBy: $orderBy
      isApprovedOnly: $isApprovedOnly
    ) {
      id
      name
      logoUrl
      goal
      progressAmount
      profileUrl
    }
  }
`)

export const matchedGivingProgressDocument = graphql(`
  query MatchedGivingProgress($campaignId: ID!, $matchedGivingId: ID!, $isLiveMode: Boolean!) {
    matchedGivingProgress(campaignId: $campaignId, matchedGivingId: $matchedGivingId, isLiveMode: $isLiveMode) {
      amount
    }
  }
`)

export const fullMatchedGivingProgressDocument = graphql(/* GraphQL */ `
  query FullMatchedGivingProgress($campaignId: ID!, $matchedGivingId: ID!, $isLiveMode: Boolean!) {
    fullMatchedGivingProgress(campaignId: $campaignId, matchedGivingId: $matchedGivingId, isLiveMode: $isLiveMode) {
      amount
    }
  }
`)

export const purlQueryDocument = graphql(/* GraphQL */ `
  query Purl($charityId: ID!, $campaignId: ID!, $id: String, $params: String) {
    purl(charityId: $charityId, campaignId: $campaignId, id: $id, params: $params)
  }
`)

export const purlParamsQueryDocument = graphql(/* GraphQL */ `
  query PurlParams($charityId: ID!, $campaignId: ID!, $params: String) {
    purlParams(charityId: $charityId, campaignId: $campaignId, params: $params)
  }
`)

export const salesforceContactQueryDocument = graphql(/* GraphQL */ `
  query SalesforceContact($charityId: ID!, $contactId: String!, $donorId: String!) {
    salesforceContact(charityId: $charityId, contactId: $contactId, donorId: $donorId) {
      firstName
      lastName
      email
      donationAmounts {
        field
        amount
      }
    }
  }
`)

export const recordStatsDocument = graphql(/* GraphQL */ `
  mutation RecordStats($input: AnalyticsInput!) {
    recordStats(input: $input)
  }
`)

export const pageMetadataDocument = graphql(/* GraphQL */ `
  query PageMetadata($charityId: ID!, $campaignId: ID!, $pageId: ID!) {
    pageMetadata(charityId: $charityId, campaignId: $campaignId, pageId: $pageId) {
      charity {
        CharityName
        Phone
        Email
        ABN
        CustomFields
        FaviconUrl
      }
      campaign {
        CampaignName
        CampaignGoal
        ogImage
        ogTitle
        gtmScriptId
        ga4ScriptId
        fbPixelId
        isConversionsApi
      }
      page {
        OgImage
        OgTitle
        OgDescription
      }
    }
  }
`)

export const paymentSettingQueryDocument = graphql(`
  query PaymentSettings($campaignId: ID!, $isLiveMode: Boolean) {
    paymentSettings(campaignId: $campaignId, isLiveMode: $isLiveMode) {
      platformFeeType
      donorPaysPlatformFees {
        platformFeeType
        givingType
        givingFrequencyType
        paymentProviderType
        feeRanges {
          startRange
          endRange
          fixFee
          percentageFee
        }
      }
      tax {
        percentage
        countryCodeIso2
        label
      }
      donationPagePaymentProviderSettings {
        stripePaymentSettings {
          publishableKey
          connectAccountId
          paymentProviderType
          paymentChargeType
        }
        payPalMerchantSetting {
          merchantId
          merchantEmail
          clientId
        }
      }
    }
  }
`)
